import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * Per-page SEO / Open Graph / indexing helper.
 *
 * Emits an explicit <meta name="robots" content="index, follow, ..."> so
 * crawlers don't have to guess — this is the "best for indexing" posture.
 * Pass noindex={true} on error/utility pages (e.g. 404) to flip it.
 *
 * JSON-LD (jsonLd + breadcrumbs) is injected directly into <head> via a
 * useEffect because react-helmet-async dedupes <script type="application/ld+json">
 * entries by type, silently dropping all but the first one.
 */
const SITE = 'https://www.agseosydney.com.au';
const DEFAULT_IMG = '/assets/logo.webp';
const DEFAULT_TITLE =
  '#𝟭 𝗦𝗘𝗢 𝗦𝘆𝗱𝗻𝗲𝘆: 𝗦𝗲𝗮𝗿𝗰𝗵 𝗘𝗻𝗴𝗶𝗻𝗲 𝗢𝗽𝘁𝗶𝗺𝗶𝘀𝗮𝘁𝗶𝗼𝗻(𝗦𝗘𝗢) 𝗦𝘆𝗱𝗻𝗲𝘆 — 𝗡𝗢 𝗟𝗢𝗖𝗞-𝗜𝗡';

const DEFAULT_ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const NOINDEX_ROBOTS = 'noindex, follow';

const absolute = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${SITE}${url.startsWith('/') ? url : `/${url}`}`;
};

/**
 * Build a clean canonical URL from a pathname.
 *  - Force the production host (so dev/preview URLs don't leak into the index).
 *  - Strip query strings + hash fragments.
 *  - Collapse duplicate slashes.
 *  - Preserve a trailing slash only on "/" — every other URL has it stripped
 *    so that /backlink-audit and /backlink-audit/ don't both end up indexable.
 */
const TRAILING_SLASH_PATHS = new Set();
const buildCanonical = (pathname, override) => {
  if (override) return override;
  let p = (pathname || '/').split('?')[0].split('#')[0];
  p = p.replace(/\/{2,}/g, '/');
  if (p !== '/' && !TRAILING_SLASH_PATHS.has(p) && p.endsWith('/')) {
    p = p.replace(/\/+$/, '');
  }
  return `${SITE}${p}`;
};

const SEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMG,
  noindex = false,
  jsonLd,
  breadcrumbs,
}) => {
  const { pathname } = useLocation();
  const finalCanonical = buildCanonical(pathname, canonical);
  const fullTitle = title
    ? `${title} | GWM`
    : DEFAULT_TITLE;
  const absoluteImage = absolute(image);

  const meta = [
    { name: 'robots', content: noindex ? NOINDEX_ROBOTS : DEFAULT_ROBOTS },
    { name: 'googlebot', content: noindex ? NOINDEX_ROBOTS : DEFAULT_ROBOTS },
    { name: 'bingbot', content: noindex ? NOINDEX_ROBOTS : DEFAULT_ROBOTS },
    { property: 'og:title', content: fullTitle },
    { property: 'og:url', content: finalCanonical },
    { property: 'og:image', content: absoluteImage },
    { property: 'og:site_name', content: 'GWM — Green Web Marketing' },
    { property: 'og:locale', content: 'en_AU' },
    { property: 'og:type', content: pathname === '/' ? 'website' : 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:image', content: absoluteImage },
  ];
  if (description) {
    meta.push({ name: 'description', content: description });
    meta.push({ property: 'og:description', content: description });
    meta.push({ name: 'twitter:description', content: description });
  }

  const link = [
    { rel: 'canonical', href: finalCanonical },
    { rel: 'alternate', hrefLang: 'en-AU', href: finalCanonical },
    { rel: 'alternate', hrefLang: 'x-default', href: finalCanonical },
  ];

  // Build combined @graph JSON-LD. Render it as a <script> directly in the
  // DOM (via dangerouslySetInnerHTML) — react-helmet-async dedupes multiple
  // script[type=application/ld+json] blocks, and Google parses JSON-LD from
  // anywhere in the document (not just <head>).
  const graphNodes = [];
  if (jsonLd) graphNodes.push(jsonLd);
  if (breadcrumbs && breadcrumbs.length > 0) {
    graphNodes.push({
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: absolute(b.path),
      })),
    });
  }
  const graphJson = graphNodes.length
    ? JSON.stringify({ '@context': 'https://schema.org', '@graph': graphNodes })
    : null;

  return (
    <>
      <Helmet title={fullTitle} meta={meta} link={link} />
      {graphJson && (
        <script
          type="application/ld+json"
          data-gwm-seo="1"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: graphJson }}
        />
      )}
    </>
  );
};

export default SEO;
